/* eslint-disable max-lines */
import isClient from '../helpers/environment';
import { isEqualByValue } from '../utils/objectUtils';
import {
    Actions,
    AddImpressedProduct,
    AdvancedEcommerceOptions,
    E_COMMERCE_CATEGORY,
    EC_LP_PRODUCT_CLICK_EVENT,
    EC_LP_PRODUCT_IMPRESSION_EVENT,
    EcommerceProduct,
    FILTERS_CATEGORY,
    HEADER_CATEGORY,
    PAGEVIEW_EVENT,
    SEARCH_CATEGORY,
    SEARCH_LOGS_CATEGORY,
    SpecificGaEvent,
    TrackECommerceOptions,
    TrackFiltersOptions,
    TrackHeaderOptions,
    TrackingConfig,
    TrackSearchLogsOptions,
    TrackSearchOptions,
    TrackUsersOptions,
    USERS_CATEGORY,
    Promotion,
    PDP_CATEGORY,
    PLP_CATEGORY,
    TrackFabricSample,
    FABRIC_SAMPLE_CATEGORY,
    LDP_CATEGORY,
    TrackPDPLoadEvents,
    LOOKS_LISTING_PAGE,
    EXPERIMENT,
    TrackLLPImpressionEvents,
} from './types';
import getImpressionHandler from './impressions';
import {
    DataLayerManagerInterface,
    DataLayerManagerInterfaceReadOnly,
    UserSessionInfo,
} from './dataLayerManager/abstractDataLayerManager';
import ShopToClubTracker from './shopToClub';
import LoginAndRegisterTracker from './loginAndRegister';
import OneHeaderTracker from './oneHeader';
import DynamicSearchTracker, { defaultSearchGaData } from './dynamicSearch';
import HomepageTracker from './homepage';
import { DataLayerManagerFactory, DataLayerManagerType } from './dataLayerManager/dataLayerManagerFactory';
import ProductRatingsOverlayTracker from './productRatingsOverlay';
import { WishListTrackingAction, WishlistTrackingParameter } from 'Client/redux/products/types';
import { FeatureToggles, Logger, MFEventOptions } from 'Common/appshell_types';
import { CommonEventOptions, DataLayerItem, TrackPageViewArguments } from 'Common/appshell_types/trackingHelpers';
import { SegmentTrackerInterface } from 'Common/segment';

export interface GaTrackingInterface {
    getDataLayerManager(): DataLayerManagerInterfaceReadOnly;
    getRegisteredEvents(): DataLayerItem[];
    trackAdvancedECommerce(event: SpecificGaEvent, options: AdvancedEcommerceOptions): this;
    // @todo: refactor this into a Factory
    shopToClub: ShopToClubTracker;
    loginAndRegister: LoginAndRegisterTracker;
    oneHeader: OneHeaderTracker;
}

export default class GaTracking implements GaTrackingInterface {
    private readonly dataLayerManager: DataLayerManagerInterface;

    private segmentTracker?: SegmentTrackerInterface;

    public config: TrackingConfig;

    public actions = Actions;

    public addImpression: AddImpressedProduct;

    public shopToClub: ShopToClubTracker;

    public loginAndRegister: LoginAndRegisterTracker;

    public oneHeader: OneHeaderTracker;

    public dynamicSearch: DynamicSearchTracker;

    public homepage: HomepageTracker;

    public ratingOverlay: ProductRatingsOverlayTracker;

    public constructor({
        type,
        trackingConfig,
        logger,
        segmentTracker,
    }: {
        type: DataLayerManagerType;
        trackingConfig: TrackingConfig;
        logger: Logger;
        segmentTracker?: SegmentTrackerInterface;
    }) {
        const { platform } = trackingConfig;
        this.dataLayerManager = DataLayerManagerFactory.createDataLayerManager({ logger, platform, type });
        this.config = trackingConfig;
        this.addImpression = getImpressionHandler(this);
        this.shopToClub = new ShopToClubTracker(this.dataLayerManager);
        this.loginAndRegister = new LoginAndRegisterTracker(this.dataLayerManager);
        this.oneHeader = new OneHeaderTracker(this.dataLayerManager);
        this.dynamicSearch = new DynamicSearchTracker(this.dataLayerManager);
        this.homepage = new HomepageTracker(this.dataLayerManager);
        this.ratingOverlay = new ProductRatingsOverlayTracker(this.dataLayerManager);
        this.segmentTracker = segmentTracker;
    }

    public getDataLayerManager(): DataLayerManagerInterfaceReadOnly {
        return this.dataLayerManager;
    }

    public getRegisteredEvents(): DataLayerItem[] {
        return this.getDataLayerManager().getRegisteredEvents();
    }

    public trackPageView({ pageType, gaData = {} }: TrackPageViewArguments): this {
        const pageViewIgnoredFields = ['event', 'gtm.uniqueEventId'];
        const data: { [key: string]: string | number } = { pageType, ...defaultSearchGaData, ...gaData };
        if (isClient()) {
            data.event = PAGEVIEW_EVENT;
        }
        const registeredEvents = this.dataLayerManager.getRegisteredEvents();
        const pageTypeEvents = registeredEvents.filter(event => !!event.pageType);
        const lastPageView = { ...pageTypeEvents[pageTypeEvents.length - 1] };

        const copiedGaData = { ...data };
        pageViewIgnoredFields.forEach(field => {
            delete copiedGaData[field];
            delete lastPageView[field];
        });

        const isTrackingDataChanged = !isEqualByValue(lastPageView, copiedGaData);

        // Only tracks when page type and page number has changed
        if (isTrackingDataChanged) {
            this.dataLayerManager.sendData(data);

            if (this.segmentTracker) {
                this.segmentTracker.page(pageType);
            }
        }
        return this;
    }

    public trackSearch(options: TrackSearchOptions): this {
        this.dataLayerManager.sendGenericEvent({
            category: SEARCH_CATEGORY,
            ...options,
            label: (options.label || '').toLocaleLowerCase(),
        });
        return this;
    }

    public trackSearchLogs(options: TrackSearchLogsOptions): this {
        this.dataLayerManager.sendGenericEvent({
            category: SEARCH_LOGS_CATEGORY,
            ...options,
        });
        return this;
    }

    public trackECommerce(options: TrackECommerceOptions): this {
        this.dataLayerManager.sendGenericEvent({
            category: E_COMMERCE_CATEGORY,
            ...options,
        });
        return this;
    }

    public trackUsers(options: TrackUsersOptions): this {
        this.dataLayerManager.sendGenericEvent({
            category: USERS_CATEGORY,
            ...options,
        });
        return this;
    }

    public trackUserLoginAndRegistration(userInfo: UserSessionInfo): this {
        if (this.dataLayerManager.getUserSessionDidInit()) {
            this.dataLayerManager.sendData(userInfo);
        } else {
            this.dataLayerManager.initUserSession(userInfo);
        }
        return this;
    }

    public trackFilters(options: TrackFiltersOptions): this {
        this.dataLayerManager.sendGenericEvent({
            category: FILTERS_CATEGORY,
            ...options,
        });
        return this;
    }

    public trackPDPLoadEvent(event: TrackPDPLoadEvents, options: CommonEventOptions, category?: string) {
        this.dataLayerManager.sendEvent(event, {
            category: category || PDP_CATEGORY,
            ...options,
        });
        return this;
    }

    public trackLLP(options: CommonEventOptions, category?: string): this {
        this.dataLayerManager.sendGenericEvent({
            category: category || LOOKS_LISTING_PAGE,
            ...options,
        });
        return this;
    }

    public trackLDP(options: CommonEventOptions, category?: string): this {
        this.dataLayerManager.sendGenericEvent({
            category: category || LDP_CATEGORY,
            ...options,
        });
        return this;
    }

    public trackLLPImpressionEvent(event: TrackLLPImpressionEvents, options: CommonEventOptions, category?: string) {
        this.dataLayerManager.sendEvent(event, {
            category: category || LOOKS_LISTING_PAGE,
            ...options,
        });
        return this;
    }

    public trackPDPInteractionEvent(options: CommonEventOptions, category?: string): this {
        this.dataLayerManager.sendGenericEvent({
            category: category || PDP_CATEGORY,
            ...options,
        });
        return this;
    }

    public trackPLP(options: CommonEventOptions): this {
        this.dataLayerManager.sendGenericEvent({
            category: PLP_CATEGORY,
            ...options,
        });
        return this;
    }

    public trackExperiment(options: CommonEventOptions): this {
        this.dataLayerManager.sendGenericEvent({
            category: EXPERIMENT,
            ...options,
        });
        return this;
    }

    public trackHeader(options: TrackHeaderOptions): this {
        this.dataLayerManager.sendGenericEvent({
            category: HEADER_CATEGORY,
            ...options,
        });
        return this;
    }

    public trackAdvancedECommerce(event: SpecificGaEvent, options: AdvancedEcommerceOptions, label?: string): this {
        this.dataLayerManager.sendSpecificEvent(event, {
            category: E_COMMERCE_CATEGORY,
            ecommerce: options,
            label,
        });
        return this;
    }

    public trackEcProductClick(product: EcommerceProduct): this {
        this.trackECommerce({
            action: Actions.ECommerce.ProductClick,
            label: product.id,
        });

        this.trackAdvancedECommerce(EC_LP_PRODUCT_CLICK_EVENT, {
            click: {
                actionField: { list: product.list },
                products: [product],
            },
        });
        return this;
    }

    public trackEcProductImpressions(products: EcommerceProduct[]): this {
        this.trackAdvancedECommerce(EC_LP_PRODUCT_IMPRESSION_EVENT, {
            impressions: products,
            currencyCode: this.config.currency,
        });
        return this;
    }

    public trackCategoryNavigationTeaserClick(trackingId: string, flyoutTeaserPromotion: Promotion[]): this {
        this.trackAdvancedECommerce(trackingId, {
            promoClick: {
                promotions: flyoutTeaserPromotion,
            },
        });
        return this;
    }

    public trackWishListHeartClick({
        action,
        sku,
        category,
        name,
        brand,
        price,
        label,
        currency,
        url,
    }: WishlistTrackingParameter): this {
        this.dataLayerManager.sendGenericEvent({
            category: E_COMMERCE_CATEGORY,
            action: action === WishListTrackingAction.ADD ? 'Add to Wishlist' : 'Remove from Wishlist',
            label,
        });

        if (this.segmentTracker && action === WishListTrackingAction.ADD) {
            const priceAsNumber = price ? parseFloat(price) : null;

            this.segmentTracker.track('Product Added to Wishlist', {
                product_id: sku,
                sku,
                category,
                name,
                brand,
                price: priceAsNumber,
                value: priceAsNumber,
                quantity: 1,
                currency,
                url,
            });
        }

        return this;
    }

    public trackFabricSample(action: TrackFabricSample, label: string): this {
        this.dataLayerManager.sendGenericEvent({
            category: FABRIC_SAMPLE_CATEGORY,
            action,
            label,
        });
        return this;
    }

    public trackLaunchDarklyVariation(action: keyof FeatureToggles | string, label: string): this {
        this.dataLayerManager.sendEvent('LaunchDarklyExperiment', {
            category: 'Experiment',
            action,
            label,
        });
        return this;
    }

    public trackGenericEvent(category: string, options: CommonEventOptions): this {
        this.dataLayerManager.sendGenericEvent({
            category,
            ...options,
        });
        return this;
    }

    public trackEvent(eventName: string, options: MFEventOptions): this {
        this.dataLayerManager.sendEvent(eventName, options);
        return this;
    }

    public pushToDatalayer(options: DataLayerItem): this {
        this.dataLayerManager.sendData(options);
        return this;
    }
}
